// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable react/prop-types */
import React from 'react';
import clsx from 'clsx';
// eslint-disable-next-line import/no-extraneous-dependencies
import { format } from 'date-fns';
import { PBSLink } from '../../../atoms/link';
import { useFilters } from '../../../../hooks/filters';
import { parseDatoLocale } from '../../../../utils/locale';
import ArrowIcon from '../../../../assets/arrow.inline.svg';

const GluDownloadContainer = ({ data }) => {
	const { locale } = data.datoCmsGluGossip;

	const downloadQuery = ({ queryName, skip }) => ` {
		${queryName}(first: 12, skip: ${skip}, locale: ${parseDatoLocale[locale]}) {
        seo {
            title
            description
            image {
                url
            }
            twitterCard
        }
        thumbnail {
            alt
            url
        }
        intro
        title
        pdf {
            url
            format
            mimeType
            filename
        }
        id
        createdAt
		}
        _${queryName}Meta(locale: ${parseDatoLocale[locale]})  {
            count
        }
	}`;

	const { pagination, setPagination, items } = useFilters({
		queryName: 'allGluDownloads',
		query: downloadQuery,
		data: data?.allDatoCmsGluDownload,
		itemLimit: 12,
	});
	const { currentPage, totalPages } = pagination;
	const nextPage = currentPage + 1;
	const prevPage = currentPage - 1;

	const disablePrev = currentPage === 0;
	const disableNext = nextPage === totalPages;
	const hasPagination = Object.keys(pagination).length;
	const isClient = typeof window === 'object';

	const disabledStyles = 'hover:text-grey-400 text-grey-400 no-underline';

	// eslint-disable-next-line no-shadow
	const onClick = ({ e, currentPage, disabled }) => {
		e.preventDefault();
		if (disabled) return;
		if (isClient) {
			const url = new URL(window.location);
			url.searchParams.set('p', currentPage + 1);
			// eslint-disable-next-line no-unused-expressions
			currentPage === 0
				? url.searchParams.delete('p')
				: url.searchParams.set('p', currentPage + 1);
			window.history.pushState({}, '', url);
		}

		setPagination({
			...pagination,
			currentPage,
		});
	};

	const renderPaginationBar = () => (
		<div className="flex flex-col sm:flex-row">
			<span className="flex flex-row items-end justify-start text-sm">
				{data?.allDatoCmsGluDownload?.totalCount} results
			</span>
			<div className="flex items-center gap-2 mx-auto mt-10 max-w-[380px] w-full md:w-1/2 lg:w-1/3 md:max-w-none">
				<div className="w-1/3">
					<PBSLink
						variant="Link"
						className={clsx(
							disablePrev && disabledStyles,
							'align-middle',
							'focus-visible:outline-none'
						)}
						onClick={(e) =>
							onClick({
								e,
								currentPage: prevPage,
								disabled: disablePrev,
							})
						}
					>
						<ArrowIcon
							viewBox="0 0 33 34"
							className="w-4 h-auto mr-3 pt-[1px]"
						/>
						Previous
					</PBSLink>
				</div>
				<div className="inline w-1/3 text-center text-base-f">
					<p>
						<span className="font-notosans-medium">{nextPage} </span>
						of {totalPages}
					</p>
				</div>
				<div className="w-1/3 text-right">
					<PBSLink
						variant="Link"
						className={clsx(
							disableNext && disabledStyles,
							'align-middle',
							'focus-visible:outline-none'
						)}
						onClick={(e) => {
							onClick({
								e,
								currentPage: nextPage,
								disabled: disableNext,
							});
						}}
					>
						Next
						<ArrowIcon
							viewBox="0 0 33 34"
							className={clsx(
								'w-4 h-auto ml-3 pt-[1px] -scale-x-100'
							)}
						/>
					</PBSLink>
				</div>
			</div>
		</div>
	);

	return items.length > 0 ? (
		<>
			<div className="grid grid-cols-1 gap-10 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
				{items.map((download) => {
					const { title, intro, thumbnail, pdf, createdAt } =
						download;
					const date = format(new Date(createdAt), 'MM/dd/yyyy');
					return (
						download.title && (
							<div
								key={download.id}
								className="flex flex-col border-2 border-blue-200 rounded-sm shadow-lg"
							>
								<img
									src={thumbnail.url}
									alt="Peninsla HR & Health and Safety Support"
									className="min-h-[163px] rounded-t-sm object-cover"
								/>
								<div className="flex flex-col justify-between h-full p-5">
									<div>
										<div className="text-2xl font-bold min-h-[80px] pt-4">
											{title}
										</div>
										<div className="py-3 text-lg font-light">
											{intro}
										</div>
									</div>

									<div>
										<div className="py-5 border-b-2 border-[#D7DDF9]">
											<PBSLink
												className="justify-center !font-normal w-full text-white-400 bg-[#3954E0] hover:bg-blue-400"
												to={pdf.url}
												variant="Default"
												target="_blank"
												size="Small"
												download={pdf.filename}
											>
												Download
												<svg
													width="31"
													height="31"
													viewBox="0 0 31 31"
													fill="none"
													xmlns="http://www.w3.org/2000/svg"
													className="ml-2"
												>
													<path
														d="M15.7504 23.4774L12.6742 20.4012C12.557 20.2693 12.4179 20.1815 12.2568 20.1375C12.0956 20.0937 11.9272 20.0937 11.7514 20.1375C11.5902 20.1815 11.4511 20.262 11.3339 20.3792C11.2167 20.4964 11.1361 20.6429 11.0922 20.8187C11.0483 20.9799 11.0483 21.1409 11.0922 21.3021C11.1361 21.4632 11.224 21.6024 11.3559 21.7196L15.0912 25.4769C15.2817 25.6527 15.5014 25.7406 15.7504 25.7406C15.9994 25.7406 16.2265 25.6527 16.4316 25.4769L20.1669 21.7196C20.3427 21.5292 20.4306 21.3094 20.4306 21.0604C20.4306 20.8114 20.3353 20.599 20.1449 20.4232C19.9692 20.2328 19.7567 20.1375 19.5077 20.1375C19.2587 20.1375 19.039 20.2255 18.8486 20.4012L15.7504 23.4774ZM14.8275 16.3802V24.8177C14.8275 25.0814 14.9155 25.3011 15.0912 25.4769C15.267 25.6527 15.4868 25.7406 15.7504 25.7406C16.0287 25.7406 16.2558 25.6527 16.4316 25.4769C16.6074 25.3011 16.6952 25.0814 16.6952 24.8177V16.3802C16.6952 16.1166 16.6074 15.8968 16.4316 15.7211C16.2558 15.5307 16.0287 15.4354 15.7504 15.4354C15.4868 15.4354 15.267 15.5307 15.0912 15.7211C14.9155 15.8968 14.8275 16.1166 14.8275 16.3802ZM13.9047 7.04186C12.1322 6.86607 10.4916 7.18101 8.98283 7.98668C7.47404 8.77769 6.30217 9.95689 5.4672 11.5243C4.66154 13.1063 4.36124 14.7469 4.56632 16.4461C4.7714 18.1453 5.45988 19.6688 6.63175 21.0164C6.80754 21.2068 7.01994 21.3094 7.26895 21.324C7.51798 21.3387 7.7377 21.2581 7.92814 21.0823C8.13322 20.9066 8.24308 20.6942 8.25773 20.4452C8.27238 20.1961 8.19181 19.9691 8.01603 19.764C7.12248 18.724 6.5878 17.5448 6.41202 16.2265C6.25089 14.9081 6.48527 13.6263 7.11515 12.3812C7.77433 11.1654 8.68986 10.2499 9.86173 9.63462C11.0336 9.00475 12.308 8.76304 13.685 8.90952C15.0619 9.08531 16.2484 9.61265 17.2446 10.4916C18.2553 11.3558 18.9364 12.4544 19.288 13.7875C19.3466 14.0072 19.4564 14.183 19.6176 14.3148C19.7934 14.432 19.9911 14.4906 20.2108 14.4906H21.3754C22.2103 14.5052 22.9501 14.7469 23.5947 15.2157C24.2392 15.6844 24.6859 16.3216 24.935 17.1273C25.1986 17.9183 25.1986 18.6947 24.935 19.4564C24.6859 20.2034 24.2246 20.826 23.5507 21.324C23.3457 21.4852 23.2212 21.6903 23.1772 21.9393C23.1332 22.1737 23.1845 22.4008 23.3309 22.6205C23.4921 22.8255 23.6899 22.95 23.9243 22.994C24.1733 23.038 24.4077 22.9867 24.6274 22.8402C25.6381 22.1078 26.3339 21.1776 26.7147 20.0497C27.0957 18.9071 27.1103 17.7425 26.7587 16.556C26.3632 15.3695 25.6821 14.4247 24.7152 13.7215C23.7484 13.0038 22.6352 12.6375 21.3754 12.6229H20.892C20.3353 11.0848 19.4345 9.82505 18.1894 8.84361C16.9589 7.84752 15.5307 7.24693 13.9047 7.04186Z"
														fill="white"
													/>
												</svg>
											</PBSLink>
										</div>
										<div className="flex items-center justify-between pt-4 pb-2">
											<p className="text-[#3954E0] bg-[#D7DDF9] px-2 py-1">
												PDF
											</p>
											<p className="text-[#3954E0] font-bold">
												{date}
											</p>
										</div>
									</div>
								</div>
							</div>
						)
					);
				})}
			</div>
			{hasPagination ? renderPaginationBar() : null}
		</>
	) : (
		<div className="flex flex-col items-center justify-center m-auto h-72">
			<h3 className="text-xl font-bold">No search results</h3>
			<p>
				No results matched this search. Try editing your search or look
				for something else.
			</p>
		</div>
	);
};

export default GluDownloadContainer;
